import React from 'react';
import T from 'Components/Helpers/translate';
import useAirscaleLookup from './useAirscaleLookup';
import CompanyPeople, { type CompanyPeopleMemory } from 'App/shadcn/pages/ProspectingTools/Lookup/CompanyPeople';
import { isLinkedInCompanyPage } from './pageCompanyDomain';

/**
 * The last search per company, for as long as the panel is open. Opening a person from the list
 * makes their profile the active tab, which swaps this company tab out of the panel; back on the
 * company, the list is shown again instead of being bought again (WEB-13771).
 */
const remembered = new Map<string, CompanyPeopleMemory>();

/** For tests: each starts with nothing remembered. */
export const forgetCompanyPeople = () => remembered.clear();

/**
 * "People at this company" in the Add to Upsales tab on a LinkedIn company page (WEB-13375): the
 * same role search as Lookup's company result (WEB-13377), for the company the page is - by the page
 * itself, not its website, which other companies' hosted pages can share (WEB-13771). Each person
 * opens their LinkedIn profile, where the Add person tab finds their email and phone and adds them.
 * Behind `useAirscaleLookup` - the flags, and not EU ONLY; the server has the last word on access.
 */
const LinkedInCompanyPeople: React.FC<{ pageUrl: string; domain: string | null | undefined }> = ({
	pageUrl,
	domain
}) => {
	const canLookUp = useAirscaleLookup();
	if (!canLookUp || !isLinkedInCompanyPage(pageUrl)) return null;
	const company = /linkedin\.com\/company\/([^/?#]+)/i.exec(pageUrl)?.[1]?.toLowerCase() ?? pageUrl;

	return (
		<CompanyPeople
			// Keyed by the company, so its own tabs keep the search and another company starts afresh.
			key={company}
			remembered={remembered.get(company)}
			onRemember={memory => remembered.set(company, memory)}
			domain={domain ?? undefined}
			linkedinCompanyUrl={pageUrl}
			actionLabel={T('prospectingAgent.openLinkedin')}
			onLookUp={profileUrl => window.open(profileUrl, '_blank', 'noopener')}
		/>
	);
};

export default LinkedInCompanyPeople;
